import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { useApi } from '../lib/api';

interface DataSettings {
  contributeToGlobalTraining: boolean;
  globalTrainingConsentAt: string | null;
  includeKeywords: boolean;
  includeMatchPatterns: boolean;
  includeCorrections: boolean;
  organizationOverride?: {
    hasOrgSettings: boolean;
    orgContributeToGlobalTraining: boolean;
    message: string;
  } | null;
}

interface DataContributionInfo {
  program: string;
  description: string;
  whatWeCollect: {
    keywords: string;
    matchPatterns: string;
    statistics: string;
  };
  whatWeNeverCollect: string[];
  yourRights: string[];
  consentDefaults: {
    free: string;
    individual: string;
    team: string;
  };
}

export function PrivacySettings() {
  const { organization } = useOrganization();
  const { get, patch } = useApi();

  const [userSettings, setUserSettings] = useState<DataSettings | null>(null);
  const [orgSettings, setOrgSettings] = useState<DataSettings | null>(null);
  const [info, setInfo] = useState<DataContributionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);

        const [userRes, infoRes] = await Promise.all([
          get<DataSettings>('/users/me/data-settings'),
          get<DataContributionInfo>('/data-contribution/info'),
        ]);

        setUserSettings(userRes);
        setInfo(infoRes);

        // If in org context, also fetch org settings
        if (organization) {
          try {
            const orgRes = await get<DataSettings>(
              `/organizations/${organization.id}/data-settings`
            );
            setOrgSettings(orgRes);
          } catch {
            // User may not have permission to view org settings
          }
        }
      } catch (err) {
        console.error('Failed to load privacy settings:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [organization, get]);

  async function updateUserSettings(updates: Partial<DataSettings>) {
    try {
      setSaving(true);
      const updated = await patch<DataSettings>('/users/me/data-settings', updates);
      setUserSettings(updated);
    } catch (err) {
      alert('Failed to update settings');
    } finally {
      setSaving(false);
    }
  }

  async function updateOrgSettings(updates: Partial<DataSettings>) {
    if (!organization) return;

    try {
      setSaving(true);
      const updated = await patch<DataSettings>(
        `/organizations/${organization.id}/data-settings`,
        updates
      );
      setOrgSettings(updated);
    } catch (err) {
      alert('Failed to update organization settings');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-white">Privacy & Data Settings</h1>
        <p className="mt-1 text-slate-400">
          Control how your data is used and contribute to improving DemoPath
        </p>
      </div>

      {/* Organization Override Notice */}
      {userSettings?.organizationOverride?.hasOrgSettings && (
        <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-blue-400 font-medium">Organization Settings Active</p>
              <p className="text-sm text-blue-300/80 mt-1">
                {userSettings.organizationOverride.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* User Data Contribution Settings */}
      <section className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-white">Data Contribution</h2>
            <p className="text-sm text-slate-400">
              Help improve matching accuracy for everyone
            </p>
          </div>
          <button
            onClick={() => setShowInfoModal(true)}
            className="text-green-500 hover:text-green-400 text-sm"
          >
            Learn more
          </button>
        </div>
        <div className="p-6 space-y-6">
          {/* Main toggle */}
          <SettingToggle
            label="Contribute to global training"
            description="Share anonymized usage patterns to improve matching for all users"
            checked={userSettings?.contributeToGlobalTraining ?? false}
            onChange={(v) => updateUserSettings({ contributeToGlobalTraining: v })}
            disabled={saving}
          />

          {userSettings?.contributeToGlobalTraining && (
            <div className="ml-8 space-y-4 border-l-2 border-slate-700 pl-6">
              <p className="text-sm text-slate-400">Choose what to include:</p>

              <SettingToggle
                label="Keyword patterns"
                description="Generic terms from agenda items (not company names or specific products)"
                checked={userSettings?.includeKeywords ?? true}
                onChange={(v) => updateUserSettings({ includeKeywords: v })}
                disabled={saving}
              />

              <SettingToggle
                label="Match patterns"
                description="How agenda items match to speech (confidence levels, not actual speech)"
                checked={userSettings?.includeMatchPatterns ?? true}
                onChange={(v) => updateUserSettings({ includeMatchPatterns: v })}
                disabled={saving}
              />

              <SettingToggle
                label="Corrections"
                description="Manual overrides you make to improve matching"
                checked={userSettings?.includeCorrections ?? true}
                onChange={(v) => updateUserSettings({ includeCorrections: v })}
                disabled={saving}
              />
            </div>
          )}

          {userSettings?.globalTrainingConsentAt && (
            <p className="text-xs text-slate-500">
              Consent given on {new Date(userSettings.globalTrainingConsentAt).toLocaleDateString()}
            </p>
          )}
        </div>
      </section>

      {/* Organization Settings (if admin/owner) */}
      {organization && orgSettings && (
        <section className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-medium text-white">Organization Data Settings</h2>
            <p className="text-sm text-slate-400">
              Manage data contribution for {organization.name}
            </p>
          </div>
          <div className="p-6 space-y-6">
            <SettingToggle
              label="Organization contributes to global training"
              description="All members' sessions will contribute anonymized data (overrides individual settings)"
              checked={orgSettings.contributeToGlobalTraining}
              onChange={(v) => updateOrgSettings({ contributeToGlobalTraining: v })}
              disabled={saving}
            />

            {orgSettings.contributeToGlobalTraining && (
              <div className="ml-8 space-y-4 border-l-2 border-slate-700 pl-6">
                <SettingToggle
                  label="Include keyword patterns"
                  checked={orgSettings.includeKeywords}
                  onChange={(v) => updateOrgSettings({ includeKeywords: v })}
                  disabled={saving}
                />
                <SettingToggle
                  label="Include match patterns"
                  checked={orgSettings.includeMatchPatterns}
                  onChange={(v) => updateOrgSettings({ includeMatchPatterns: v })}
                  disabled={saving}
                />
                <SettingToggle
                  label="Include corrections"
                  checked={orgSettings.includeCorrections}
                  onChange={(v) => updateOrgSettings({ includeCorrections: v })}
                  disabled={saving}
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Data Retention Info */}
      <section className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <h2 className="text-lg font-medium text-white mb-4">Data Retention</h2>
        <div className="space-y-4 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
            <div>
              <p className="text-white">Session data</p>
              <p className="text-slate-400">
                Stored for 1 year on Team plans. Individual plans do not store session history.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
            <div>
              <p className="text-white">Transcripts</p>
              <p className="text-slate-400">
                Only stored on Team plans. Automatically deleted after 1 year.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
            <div>
              <p className="text-white">Contributed training data</p>
              <p className="text-slate-400">
                Anonymized and cannot be traced back to you. Retained indefinitely to improve matching.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Links */}
      <div className="flex gap-6 text-sm">
        <Link to="/privacy" className="text-green-500 hover:text-green-400">
          Privacy Policy
        </Link>
        <a href="mailto:privacy@demopath.ai" className="text-green-500 hover:text-green-400">
          Contact Privacy Team
        </a>
      </div>

      {/* Info Modal */}
      {showInfoModal && info && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-slate-700 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800">
              <h2 className="text-lg font-medium text-white">{info.program}</h2>
              <button
                onClick={() => setShowInfoModal(false)}
                className="text-slate-400 hover:text-white text-xl"
              >
                &times;
              </button>
            </div>
            <div className="px-6 py-4 space-y-6">
              <p className="text-slate-300">{info.description}</p>

              <div>
                <h3 className="text-white font-medium mb-2">What We Collect</h3>
                <div className="space-y-2 text-sm">
                  <p className="text-slate-400">
                    <span className="text-slate-300">Keywords:</span> {info.whatWeCollect.keywords}
                  </p>
                  <p className="text-slate-400">
                    <span className="text-slate-300">Match Patterns:</span> {info.whatWeCollect.matchPatterns}
                  </p>
                  <p className="text-slate-400">
                    <span className="text-slate-300">Statistics:</span> {info.whatWeCollect.statistics}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-white font-medium mb-2">What We Never Collect</h3>
                <ul className="list-disc list-inside text-sm text-slate-400 space-y-1">
                  {info.whatWeNeverCollect.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-white font-medium mb-2">Your Rights</h3>
                <ul className="list-disc list-inside text-sm text-slate-400 space-y-1">
                  {info.yourRights.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-white font-medium mb-2">Default Settings by Plan</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="bg-slate-900/50 p-3 rounded">
                    <p className="text-white font-medium">Free</p>
                    <p className="text-slate-400">{info.consentDefaults.free}</p>
                  </div>
                  <div className="bg-slate-900/50 p-3 rounded">
                    <p className="text-white font-medium">Individual</p>
                    <p className="text-slate-400">{info.consentDefaults.individual}</p>
                  </div>
                  <div className="bg-slate-900/50 p-3 rounded">
                    <p className="text-white font-medium">Team</p>
                    <p className="text-slate-400">{info.consentDefaults.team}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-slate-700">
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingToggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-white font-medium">{label}</p>
        {description && <p className="text-sm text-slate-400 mt-0.5">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!checked)}
        disabled={disabled}
        className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors ${
          checked ? 'bg-green-600' : 'bg-slate-600'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span
          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
            checked ? 'translate-x-6' : ''
          }`}
        />
      </button>
    </div>
  );
}
